import { productEvent } from "@shared/analytics/productEvents";
import { TabsMenu, TabTrigger, Text } from "@solomon-labs/design/components/ui";
import React from "react";

import type { SecurityFlow, SecurityFlowIcon } from "../securityControlFlows";
import {
    GOVERNANCE_WORKFLOWS,
    type GovernanceWorkflow,
    type GovernanceWorkflowStage
} from "../securityGovernanceWorkflows";

import { SecurityJourney } from "./SecurityMap";

const AUTHORITY_ICONS: Record<
    GovernanceWorkflowStage["authority"],
    SecurityFlowIcon
> = {
    Governance: "bank",
    Operations: "gear",
    Emergency: "triangle",
    Enforcement: "shield"
};

function titleCase(value: string): string {
    return value.replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function stageRoute(
    workflow: GovernanceWorkflow,
    stageIndex: number
): SecurityFlow {
    const stage = workflow.stages[stageIndex];
    const immediateAction =
        stage.authority === "Emergency"
            ? { label: "Pause Program", icon: "pause" as const }
            : stage.authority === "Enforcement"
              ? { label: "Freeze Account", icon: "snowflake" as const }
              : undefined;
    const authority = {
        label: `${stage.authority} Authority`,
        icon: AUTHORITY_ICONS[stage.authority],
        emergency: stage.authority === "Emergency"
    };

    return {
        id: `${workflow.id}-${stageIndex}`,
        label: `${workflow.label}: ${stage.tabLabel ?? stage.title}`,
        outcome: workflow.outcome,
        steps:
            stage.mode === "governed"
                ? [
                      authority,
                      {
                          label: "USDv Controls Program",
                          icon: "chancery",
                          actions: [
                              { label: "Propose", icon: "propose" },
                              {
                                  label: `${stage.waitHours} Hour Timelock`,
                                  icon: "clock"
                              },
                              { label: "Accept", icon: "check" },
                              { label: "Applied", icon: "shield-check" }
                          ]
                      }
                  ]
                : immediateAction
                  ? [
                        authority,
                        {
                            label: "USDv Controls Program",
                            icon: "chancery",
                            actions: [
                                immediateAction,
                                { label: "Applied", icon: "shield-check" }
                            ]
                        }
                    ]
                  : [
                        authority,
                        {
                            label: "USDv Controls Program",
                            icon: "chancery",
                            actions: [{ label: "Add Setting", icon: "plus" }]
                        }
                    ],
        gates: []
    };
}

function StageTabs({
    stages,
    activeIndex,
    onSelect
}: {
    stages: GovernanceWorkflow["stages"];
    activeIndex: number;
    onSelect: (index: number) => void;
}): React.ReactElement {
    const tabClassName =
        "min-w-0 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-foreground-base";

    // One TabsMenu per stage, stacked. The DS vertical orientation measures
    // its sliding indicator on the x axis, so a single vertical list would
    // park a static accent bar under the whole column instead of under the
    // active row. The hover fill runs from the top so it resolves downward
    // out of the accent rule instead of climbing to meet it. No `border`:
    // once the gap separates the rows a per-row
    // baseline reads as a floating underline, so the accent rule on the
    // selected row is the only rule. The column hugs the longest label so
    // every stage stays on one line; the wrapper scrolls rather than
    // bleeding if a container is ever narrower than that.
    return (
        <div className="max-w-full overflow-x-auto overflow-y-hidden [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            <div className="flex w-max flex-col gap-2">
                {stages.map((stage, index) => (
                    <TabsMenu
                        key={stage.title}
                        value={String(activeIndex)}
                        onValueChange={(value): void => onSelect(Number(value))}
                        fullWidth
                        indicatorPosition="top"
                    >
                        <TabTrigger
                            value={String(index)}
                            size="xs"
                            interaction="fillFromTop"
                            className={`${tabClassName} w-full whitespace-nowrap text-left`}
                        >
                            {stage.tabLabel ?? `Step ${index + 1}`}
                        </TabTrigger>
                    </TabsMenu>
                ))}
            </div>
        </div>
    );
}

/**
 * The Program Controls example picker: a list of control examples beside the
 * route graphic for the selected one. Renders no heading of its own; it sits
 * under the "See What a Key Can Actually Do" title.
 */
export function SecurityGovernanceWorkflows(): React.ReactElement {
    const [activeId, setActiveId] = React.useState("reserve-withdrawal-access");
    const [stageIndex, setStageIndex] = React.useState(0);
    const id = React.useId();
    const workflow =
        GOVERNANCE_WORKFLOWS.find((item) => item.id === activeId) ??
        GOVERNANCE_WORKFLOWS[0];
    const route = React.useMemo(
        () => stageRoute(workflow, stageIndex),
        [workflow, stageIndex]
    );

    return (
        <div className="grid items-start gap-8 lg:grid-cols-5 lg:gap-12">
            <div
                className="min-w-0 lg:col-span-2"
                aria-label="Control examples"
            >
                {GOVERNANCE_WORKFLOWS.map((item) => {
                    const selected = item.id === workflow.id;
                    return (
                        <div
                            key={item.id}
                            className="border-t border-line-base last:border-b"
                        >
                            <h3>
                                <button
                                    type="button"
                                    id={`${id}-${item.id}`}
                                    aria-expanded={selected}
                                    aria-controls={`${id}-description-${item.id} ${id}-detail-${item.id}`}
                                    onClick={() => {
                                        productEvent("stats:selected", {
                                            entry_point: "stats_page",
                                            section: "governance",
                                            target: item.id
                                        });
                                        setActiveId(item.id);
                                        setStageIndex(0);
                                    }}
                                    className="flex w-full items-center justify-between gap-3 py-5 text-left transition-colors duration-200 motion-reduce:transition-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-foreground-base"
                                >
                                    <Text
                                        variant="body"
                                        render={<span />}
                                        className={
                                            selected
                                                ? "text-accent-base"
                                                : "text-foreground-muted"
                                        }
                                    >
                                        {titleCase(item.label)}
                                    </Text>
                                    <span
                                        aria-hidden
                                        className={`size-1.5 shrink-0 ${selected ? "bg-accent-base" : "bg-line-base"}`}
                                    />
                                </button>
                            </h3>
                            {/* The selected action's description opens under
                                its row. Height rides grid-template-rows (0fr to
                                1fr), the motion guide's CSS expand pattern, as
                                in the shared accordion-group; arbitrary values
                                because Tailwind has no 0fr row utility. */}
                            <div
                                id={`${id}-description-${item.id}`}
                                aria-hidden={!selected}
                                className={`grid transition-[grid-template-rows] duration-300 ease-[var(--ease-out-cubic)] motion-reduce:transition-none ${
                                    selected
                                        ? "grid-rows-[1fr]"
                                        : "grid-rows-[0fr]"
                                }`}
                            >
                                <div className="min-h-0 overflow-hidden">
                                    <Text
                                        variant="small"
                                        render={<p />}
                                        className={`pb-5 text-foreground-muted transition-opacity duration-300 motion-reduce:transition-none ${
                                            selected
                                                ? "opacity-100"
                                                : "opacity-0"
                                        }`}
                                    >
                                        {item.description}
                                    </Text>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
            <div className="min-w-0 lg:col-span-3">
                {GOVERNANCE_WORKFLOWS.map((item) => {
                    const selected = item.id === workflow.id;
                    return (
                        <div
                            key={item.id}
                            id={`${id}-detail-${item.id}`}
                            role="region"
                            aria-labelledby={`${id}-${item.id}`}
                            hidden={!selected}
                        >
                            {selected && (
                                <SecurityJourney
                                    route={route}
                                    topLeft={
                                        item.stages.length > 1 ? (
                                            <StageTabs
                                                stages={item.stages}
                                                activeIndex={stageIndex}
                                                onSelect={(index) => {
                                                    productEvent(
                                                        "stats:selected",
                                                        {
                                                            entry_point:
                                                                "stats_page",
                                                            section:
                                                                "governance_stage",
                                                            page: index + 1,
                                                            target: item.id
                                                        }
                                                    );
                                                    setStageIndex(index);
                                                }}
                                            />
                                        ) : undefined
                                    }
                                />
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
